import { isArray, isAsyncFn, isGeneratorFn, noop, toArr } from '@rehaje/shared'

import { UnknownSchema } from '../../constants'
import {
  SchemaAsyncFunctionError,
  SchemaFunctionParametersError,
  SchemaGeneratorFunctionError,
  SchemaTransformError
} from '../../errors'
import { SchemaDefinition } from '../../models/SchemaDefinition'

import type {
  FunctionSpecific,
  SchemaFunctionSpecific,
  TypedSchema
} from '../../types'

export class SchemaFunctionDefinition extends SchemaDefinition {
  __r_getInParameters(schema: SchemaFunctionSpecific, path: PropertyKey[]) {
    const parameters = schema.parameters ?? []
    const additionalParameters = schema.additionalParameters
    const result = toArr(parameters).reduce<TypedSchema | undefined>(
      (predicate, property) => {
        if (predicate) return predicate
        return path[0] === property.name
          ? super.getIn(property, path.slice(1))
          : predicate
      },
      undefined
    )
    if (result) return result
    if (additionalParameters)
      return super.getIn(additionalParameters, path.slice(1))
  }

  __r_getInReturn(schema: SchemaFunctionSpecific, path: PropertyKey[]) {
    const returnSchema = schema.return
    if (returnSchema) return super.getIn(returnSchema, path.slice(1))
  }

  override defaultsMerge(
    value: any,
    schema: SchemaFunctionSpecific,
    initialize: boolean
  ) {
    if (initialize) {
      if (value === undefined) return schema.default
      return value
    }
    if (schema.required && value === undefined) {
      return schema.default
    }
    return value
  }

  override getIn(schema: SchemaFunctionSpecific, path: PropertyKey[]) {
    if (path.length === 0) return schema
    const parameter = this.__r_getInParameters(schema, path)
    const _return = this.__r_getInReturn(schema, path)
    return parameter ?? _return ?? UnknownSchema
  }

  override infer(
    target: any,
    schema: SchemaFunctionSpecific,
    path: PropertyKey[] = []
  ): SchemaFunctionSpecific {
    const value =
      (typeof target === 'function' ? target : schema?.value) ?? noop
    const async = value.constructor.name === 'AsyncFunction'
    const generator = value.constructor.name === 'GeneratorFunction'

    return {
      async,
      generator,
      value,
      ...this.inferOnlySchema(schema, path)
    }
  }

  override inferOnlySchema(
    schema: SchemaFunctionSpecific,
    path: PropertyKey[]
  ) {
    return {
      ...schema,
      additionalParameters: schema.additionalParameters
        ? super.inferOnlySchema(
            schema.additionalParameters,
            path.concat('additionalParameters')
          )
        : undefined,
      parameters:
        toArr(schema.parameters)?.map((property, index) =>
          super.inferOnlySchema(property, path.concat(property.name ?? index))
        ) ?? [],
      return: schema.return
        ? super.inferOnlySchema(schema.return, path.concat('return'))
        : undefined
    }
  }

  override inferTypeScript(
    schema: SchemaFunctionSpecific,
    path: PropertyKey[]
  ): string {
    const { additionalParameters, async, parameters } = schema
    let func = ''

    if (isArray(parameters)) {
      func += `(${parameters
        .map((property, index) => {
          const name = property.name || `arg${index}`
          return `${name}:${super.inferTypeScript(property, path.concat(['parameters', name]))}`
        })
        .join(',')}`
      if (additionalParameters) {
        func += `${parameters.length > 0 ? ',' : ''}...${additionalParameters.name || 'args'}:${super.inferTypeScript(additionalParameters, path.concat('additionalParameters'))}[]`
      }
      func += ')'
    } else if (parameters) {
      func += `(...${super.inferTypeScript(parameters, path.concat('parameters'))})`
    } else if (additionalParameters) {
      func += `(...${additionalParameters.name || 'args'}:${super.inferTypeScript(additionalParameters, path.concat('additionalParameters'))}[])`
    } else {
      func += '()'
    }

    let returnType = 'void'

    if (schema.return) {
      returnType =
        `${super.inferTypeScript(schema.return, path.concat('return'))}` ||
        'void'
      // 如果是异步函数,用 Promise 包裹返回类型
      if (async) {
        returnType = `Promise<${returnType}>`
      }
    } else {
      // void 类型也需要 Promise 包裹
      if (async) {
        returnType = `Promise<${returnType}>`
      }
    }

    return `${func}=>${returnType}`
  }

  override prune(target: any): any {
    return target
  }

  override transform(
    target: any,
    schema: SchemaFunctionSpecific,
    path: PropertyKey[] = []
  ) {
    if (target === null && schema.nullable) return target
    if (this.test(target, schema, path)) return target
    if (target !== schema.fallback)
      this.raise(new SchemaTransformError(schema.name))
    return target
  }

  override validate(
    target: any,
    schema: SchemaFunctionSpecific,
    path: PropertyKey[],
    parent: any,
    recursion: boolean
  ) {
    const functionType = typeof target === 'function'
    const specificType = this.isRelationSpecific<FunctionSpecific>(target)
    if (this.validateMetadata(target, schema, functionType || specificType))
      return
    if (functionType) {
      if (schema.async && !isAsyncFn(target)) {
        this.raise(new SchemaAsyncFunctionError(schema.name))
      }
      if (schema.generator && !isGeneratorFn(target)) {
        this.raise(new SchemaGeneratorFunctionError(schema.name))
      }
    } else if (specificType) {
      if (!recursion) return
      if (!isArray(target.parameters)) {
        this.raise(new SchemaFunctionParametersError(schema.name))
        return
      }
      if (isArray(schema.parameters)) {
        for (let i = 0; i < schema.parameters.length; i++) {
          super.validate(
            target.parameters[i],
            schema.parameters[i],
            path.concat(i),
            target.parameters
          )
        }
        if (schema.additionalParameters) {
          for (
            let i = schema.parameters.length;
            i < target.parameters.length;
            i++
          ) {
            super.validate(
              target.parameters[i],
              schema.additionalParameters,
              path.concat(i),
              target.parameters
            )
          }
        }
      } else if (schema.parameters) {
        for (let i = 0; i < target.parameters.length; i++) {
          super.validate(
            target.parameters[i],
            schema.parameters,
            path.concat(i),
            target.parameters
          )
        }
      } else if (schema.additionalParameters) {
        for (let i = 0; i < target.parameters.length; i++) {
          super.validate(
            target.parameters[i],
            schema.additionalParameters,
            path.concat(i),
            target.parameters
          )
        }
      }
    }
  }
}
